// src/utils/viewAs.ts
//
// What a "view as the merchant" session looks like from the merchant's tab.
//
// Staff open the merchant's screens with a token the backend issues for the
// merchant's owner (ourteam-backend#5712): it carries `imp`, the staff user's
// id, expires after about two hours, and every write made with it is refused
// 403 `view_as_read_only`. The tab holds nothing else that says "this is staff
// looking", so the token itself is the source of truth.
//
// The claims are read for presentation only (a banner, disabled buttons). The
// server is what refuses the writes; nothing here is a security boundary, so the
// signature is deliberately not checked.

import { getCookie } from "./cookie";

/** `data.code` of the 403 every write answers with while viewing as the client. */
export const VIEW_AS_REFUSED_CODE = "view_as_read_only";

/** Fired on `window` with `detail: string` when a write was refused for being read-only. */
export const VIEW_AS_REFUSED_EVENT = "oura:view-as-refused";

/** Set when a view-as session ran out, so the login screen can say so. */
export const VIEW_AS_ENDED_FLAG = "__view_as_ended__";

/**
 * Set by /switch-workspace when the staff tab said the owner behind the session
 * is a placeholder (the merchant has no login yet). A hint for one sentence in
 * the banner, never an input to anything the server decides.
 */
export const VIEW_AS_PLACEHOLDER_FLAG = "__view_as_placeholder__";

export interface ViewAsState {
    /** The token carries an impersonation claim. */
    active: boolean;
    /** Epoch milliseconds the session ends, or null when the token has no `exp`. */
    expiresAt: number | null;
    /** The staff tab reported a placeholder owner. Unknown reads as false: the sentence just is not shown. */
    placeholder: boolean;
}

export const NOT_VIEW_AS: ViewAsState = { active: false, expiresAt: null, placeholder: false };

interface Claims {
    imp?: unknown;
    exp?: unknown;
}

export function readClaims(token: string | null | undefined): Claims | null {
    if (!token) return null;
    const part = token.split(".")[1];
    if (!part) return null;
    try {
        const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
        const json = decodeURIComponent(
            atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, "="))
                .split("")
                .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
                .join(""),
        );
        const claims = JSON.parse(json);
        return claims && typeof claims === "object" ? (claims as Claims) : null;
    } catch {
        return null;
    }
}

/** Whether a token is a view-as token, and when it ends. */
export function viewAsFromToken(token: string | null | undefined): ViewAsState {
    const claims = readClaims(token);
    if (!claims || typeof claims.imp !== "string" || claims.imp === "") return NOT_VIEW_AS;
    let placeholder = false;
    try {
        placeholder = sessionStorage.getItem(VIEW_AS_PLACEHOLDER_FLAG) === "1";
    } catch {
        /* storage blocked: the sentence is simply not shown */
    }
    return {
        active: true,
        expiresAt: typeof claims.exp === "number" ? claims.exp * 1000 : null,
        placeholder,
    };
}

/** The current tab's session. */
export function readViewAs(): ViewAsState {
    return viewAsFromToken(getCookie("authToken"));
}
